// src/services/AdminNotificationService.js - Para panel web React (Usando tokens de users.expoTokens)
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../../../firebase';

const EXPO_PUSH_URL = 'https://exp.host/--/api/v2/push/send';

class AdminNotificationService {
  
  // Configurar FCM con la service account key
  static FCM_SERVER_KEY = process.env.REACT_APP_FCM_SERVER_KEY || 'YOUR_FCM_SERVER_KEY_HERE';
  static FCM_PROJECT_ID = process.env.REACT_APP_FCM_PROJECT_ID || 'your-project-id';

  /**
   * Obtiene estadísticas de tokens registrados desde users.expoTokens
   */
  static async getTokensStats() {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('active', '==', true)
      );
      
      const usersSnapshot = await getDocs(usersQuery);
      let totalTokens = 0;
      let uniqueUsers = 0;
      let iosTokens = 0;
      let androidTokens = 0;
      let validTokens = 0;
      let fcmTokens = 0;
      let expoTokens = 0;
      
      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        if (userData.expoTokens && userData.expoTokens.length > 0) {
          uniqueUsers++;
          userData.expoTokens.forEach(tokenObj => {
            if (tokenObj.active && tokenObj.token) {
              totalTokens++;
              
              if (this.isValidToken(tokenObj.token)) {
                validTokens++;
              }
              
              if (tokenObj.token.startsWith('Expo')) {
                expoTokens++;
              } else {
                fcmTokens++;
              }
              
              const platform = tokenObj.deviceInfo?.platform;
              if (platform === 'ios') {
                iosTokens++;
              } else if (platform === 'android') {
                androidTokens++;
              }
            }
          });
        }
      });
      
      return {
        total: totalTokens,
        uniqueUsers,
        ios: iosTokens,
        android: androidTokens,
        valid: validTokens,
        fcmTokens,
        expoTokens
      };
    } catch (error) {
      console.error('Error obteniendo estadísticas de tokens:', error);
      return { total: 0, ios: 0, android: 0, valid: 0, uniqueUsers: 0, fcmTokens: 0, expoTokens: 0 };
    }
  }
  
  /**
   * Obtiene usuarios que tienen hijos en los cursos especificados
   */
  static async getUsersByCourses(courses) {
    try {
      if (!courses || courses.length === 0) {
        return [];
      }
      
      // Buscar hijos en los cursos especificados
      const childrenQuery = query(
        collection(db, 'children'),
        where('curso', 'in', courses)
      );
      
      const childrenSnapshot = await getDocs(childrenQuery);
      const parentIds = new Set();
      
      childrenSnapshot.forEach(doc => {
        const child = doc.data();
        if (child.parentId) {
          parentIds.add(child.parentId);
        }
      });
      
      // Obtener datos de los padres
      const users = [];
      for (const parentId of parentIds) {
        try {
          const userDoc = await getDoc(doc(db, 'users', parentId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            users.push({
              id: userDoc.id,
              email: userData.email,
              name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
              expoTokens: userData.expoTokens || [],
              ...userData
            });
          }
        } catch (error) {
          console.warn(`Error obteniendo usuario ${parentId}:`, error);
        }
      }
      
      return users.sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
    } catch (error) {
      console.error('Error obteniendo usuarios por cursos:', error);
      throw error;
    }
  }
  
  /**
   * Obtiene dispositivos de un usuario específico desde users.expoTokens
   */
  static async getUserDevices(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return [];
      }
      
      const userData = userDoc.data();
      const tokens = userData.expoTokens || [];
      
      return tokens
        .filter(tokenObj => tokenObj.active && tokenObj.token)
        .map(tokenObj => ({
          id: tokenObj.token, // Usar el token como ID
          token: tokenObj.token,
          userId: userId,
          platform: tokenObj.deviceInfo?.platform || 'unknown',
          deviceInfo: tokenObj.deviceInfo || {},
          lastUpdated: tokenObj.lastUpdated,
          displayName: this.getDeviceDisplayName(tokenObj)
        }));
    } catch (error) {
      console.error('Error obteniendo dispositivos del usuario:', error);
      throw error;
    }
  }

  /**
   * Obtiene todos los usuarios activos (CORREGIDO)
   */
  static async getAllActiveUsers() {
    try {
      // Quitar orderBy para evitar problemas con índices y campos faltantes
      const usersQuery = query(
        collection(db, 'users'),
        where('active', '==', true)
      );
      
      const usersSnapshot = await getDocs(usersQuery);
      const users = [];
      
      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        
        // Asegurar que el usuario tenga email o al menos algún identificador
        const email = userData.email || userData.emailAddress || `usuario_${doc.id}`;
        const firstName = userData.firstName || '';
        const lastName = userData.lastName || '';
        
        users.push({
          id: doc.id,
          email: email,
          name: `${firstName} ${lastName}`.trim() || email,
          firstName: firstName,
          lastName: lastName,
          expoTokens: userData.expoTokens || [],
          active: userData.active,
          ...userData
        });
      });
      
      // Ordenar manualmente por nombre o email
      return users.sort((a, b) => {
        const nameA = a.name || a.email || '';
        const nameB = b.name || b.email || '';
        return nameA.localeCompare(nameB);
      });
    } catch (error) {
      console.error('Error obteniendo usuarios activos:', error);
      throw error;
    }
  }
  
  /**
   * Obtiene tokens con filtros avanzados usando users.expoTokens (CORREGIDO)
   */
  static async getTargetTokens(notification, filters = {}) {
    try {
      let targetUserIds = [];
      
      // FILTRO 1: Usuario específico
      if (filters.specificUserId) {
        targetUserIds = [filters.specificUserId];
        console.log(`🎯 Filtro por usuario específico: ${filters.specificUserId}`);
      }
      
      // FILTRO 2: Curso específico - también desde filtros generales
      else if (filters.targetType === 'course' && filters.courses && filters.courses.length > 0) {
        console.log(`🎯 Filtro por cursos desde filters: ${filters.courses}`);
        const childrenSnapshot = await getDocs(
          query(collection(db, 'children'), where('curso', 'in', filters.courses))
        );
        const parentIds = [...new Set(childrenSnapshot.docs.map(doc => doc.data().parentId))];
        targetUserIds = parentIds;
        console.log(`👨‍👩‍👧‍👦 Padres encontrados: ${parentIds.length}`);
      }
      
      // FILTRO 3: Curso específico desde notificación
      else if (notification.tipo === 'curso' && notification.cursos && notification.cursos.length > 0) {
        console.log(`🎯 Filtro por cursos desde notificación: ${notification.cursos}`);
        const childrenSnapshot = await getDocs(
          query(collection(db, 'children'), where('curso', 'in', notification.cursos))
        );
        const parentIds = [...new Set(childrenSnapshot.docs.map(doc => doc.data().parentId))];
        targetUserIds = parentIds;
        console.log(`👨‍👩‍👧‍👦 Padres encontrados: ${parentIds.length}`);
      }
      
      // FILTRO 4: Todos los usuarios activos
      else {
        console.log(`🎯 Filtro: todos los usuarios activos`);
        const usersSnapshot = await getDocs(
          query(collection(db, 'users'), where('active', '==', true))
        );
        targetUserIds = usersSnapshot.docs.map(doc => doc.id);
        console.log(`👥 Usuarios activos encontrados: ${targetUserIds.length}`);
      }
      
      // Obtener tokens desde users.expoTokens
      const tokens = [];
      let usersProcessed = 0;
      let usersWithTokens = 0;
      
      for (const userId of targetUserIds) {
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          usersProcessed++;
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            if (userData.expoTokens && Array.isArray(userData.expoTokens)) {
              let userHasValidTokens = false;
              
              userData.expoTokens.forEach(tokenObj => {
                // Verificar que el token esté activo y sea válido
                if (tokenObj.active && tokenObj.token && this.isValidToken(tokenObj.token)) {
                  
                  // FILTRO 5: Dispositivo específico
                  if (filters.specificDeviceId && tokenObj.token !== filters.specificDeviceId) {
                    console.log(`⏭️ Saltando token ${tokenObj.token.slice(-8)} - no coincide con dispositivo específico`);
                    return;
                  }
                  
                  // FILTRO 6: Plataforma específica (iOS/Android)
                  if (filters.platform && tokenObj.deviceInfo?.platform !== filters.platform) {
                    console.log(`⏭️ Saltando token ${tokenObj.token.slice(-8)} - plataforma ${tokenObj.deviceInfo?.platform} no coincide con ${filters.platform}`);
                    return;
                  }
                  
                  tokens.push({
                    id: `${userId}_${tokenObj.token.slice(-8)}`, // ID único para el token
                    token: tokenObj.token,
                    userId: userId,
                    platform: tokenObj.deviceInfo?.platform || 'unknown',
                    deviceInfo: tokenObj.deviceInfo || {},
                    lastUpdated: tokenObj.lastUpdated
                  });
                  
                  userHasValidTokens = true;
                }
              });
              
              if (userHasValidTokens) {
                usersWithTokens++;
              }
            } else {
              console.log(`⚠️ Usuario ${userId} no tiene expoTokens o no es array`);
            }
          } else {
            console.log(`⚠️ Usuario ${userId} no existe`);
          }
        } catch (error) {
          console.warn(`❌ Error obteniendo tokens del usuario ${userId}:`, error);
        }
      }
      
      // Eliminar tokens duplicados
      const uniqueTokens = tokens.filter((token, index, self) => 
        index === self.findIndex(t => t.token === token.token)
      );
      
      console.log(`📊 Resumen: ${usersProcessed} usuarios procesados, ${usersWithTokens} con tokens válidos, ${uniqueTokens.length} tokens únicos`);
      
      return uniqueTokens;
    } catch (error) {
      console.error('❌ Error obteniendo tokens objetivo:', error);
      return [];
    }
  }

  // Validar tokens (FCM + Expo)
  static isValidToken(token) {
    return token && (
      // Expo tokens
      token.startsWith('ExponentPushToken[') || 
      token.startsWith('ExpoPushToken[') ||
      // FCM tokens (más largos, caracteres alfanuméricos, guiones y barras bajas)
      token.match(/^[a-zA-Z0-9_:-]{140,}$/) ||
      // FCM legacy format
      token.match(/^[a-zA-Z0-9_-]{22}$/)
    );
  }

  // Generar nombre descriptivo para el dispositivo
  static getDeviceDisplayName(tokenData) {
    const platform = tokenData.deviceInfo?.platform || 'unknown';
    const deviceInfo = tokenData.deviceInfo || {};
    const lastUpdated = tokenData.lastUpdated ? 
      (tokenData.lastUpdated.seconds ? 
        new Date(tokenData.lastUpdated.seconds * 1000).toLocaleDateString() : 
        new Date(tokenData.lastUpdated).toLocaleDateString()
      ) : 'Desconocida';
    
    let displayName = platform.toUpperCase();
    
    if (deviceInfo.deviceName) {
      displayName += ` - ${deviceInfo.deviceName}`;
    }
    
    if (deviceInfo.modelName) {
      displayName += ` (${deviceInfo.modelName})`;
    }
    
    if (deviceInfo.osVersion) {
      displayName += ` - ${deviceInfo.osVersion}`;
    }
    
    displayName += ` - Última actualización: ${lastUpdated}`;
    
    return displayName;
  }

  /**
   * Envía notificaciones push a través de Expo
   */
  static async sendExpoPushNotifications(tokens, notification) {
    try {
      if (!tokens || tokens.length === 0) return [];

      const N8N_URL = process.env.REACT_APP_N8N_NOTIFICATION_WEBHOOK_URL;
      if (!N8N_URL) throw new Error('REACT_APP_N8N_NOTIFICATION_WEBHOOK_URL no configurada');

      console.log(`🚀 Enviando ${tokens.length} notificaciones via n8n proxy...`);

      const title = notification.tipo === 'urgent'
        ? `🚨 URGENTE: ${notification.título}`
        : notification.título || 'Nueva notificación AMPA';

      const messages = tokens.map(tokenInfo => ({
        to: tokenInfo.token,
        sound: 'default',
        title,
        body: notification.descripción || '',
        data: {
          type: 'notification',
          notificationId: notification.id || '',
          urgent: (notification.tipo === 'urgent').toString(),
          category: notification.tipo || 'general',
          courses: notification.cursos ? JSON.stringify(notification.cursos) : '[]',
          links: notification.enlaces ? JSON.stringify(notification.enlaces) : '[]'
        },
        priority: notification.tipo === 'urgent' ? 'high' : 'normal',
        badge: 1,
        channelId: notification.tipo === 'urgent' ? 'urgent' : 'default'
      }));

      // El proxy n8n procesa un mensaje por llamada → enviar en paralelo con límite de concurrencia
      const CONCURRENCY = 10;
      const results = [];
      let totalSent = 0;
      let totalFailed = 0;

      for (let i = 0; i < messages.length; i += CONCURRENCY) {
        const batch = messages.slice(i, i + CONCURRENCY);
        const batchResults = await Promise.allSettled(
          batch.map(msg =>
            fetch(N8N_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ messages: [msg] })
            }).then(r => r.json())
          )
        );

        batchResults.forEach((res, idx) => {
          if (res.status === 'fulfilled') {
            totalSent++;
            results.push(res.value);
          } else {
            totalFailed++;
            console.warn(`❌ Error enviando al token ${batch[idx].to}:`, res.reason);
          }
        });
      }

      console.log(`✅ Completado: ${totalSent} enviadas, ${totalFailed} fallidas`);
      return results;
    } catch (error) {
      console.error('Error enviando notificaciones push:', error);
      throw error;
    }
  }

  // Enviar notificaciones push usando Firebase FCM
  static async sendFCMNotifications(tokens, notification) {
    try {
      const messages = tokens.map(tokenInfo => ({
        to: tokenInfo.token,
        notification: {
          title: notification.tipo === 'urgent' ? `🚨 URGENTE: ${notification.título}` : notification.título || 'Nueva notificación AMPA',
          body: notification.descripción || '',
          icon: 'ic_notification',
          color: notification.tipo === 'urgent' ? '#FF5252' : '#2196F3'
        },
        data: {
          type: 'notification',
          notificationId: notification.id || '',
          urgent: (notification.tipo === 'urgent').toString(),
          category: notification.tipo || 'general',
          courses: notification.cursos ? JSON.stringify(notification.cursos) : '[]',
          links: notification.enlaces ? JSON.stringify(notification.enlaces) : '[]'
        },
        priority: notification.tipo === 'urgent' ? 'high' : 'normal',
        android: {
          priority: notification.tipo === 'urgent' ? 'high' : 'normal',
          notification: {
            channel_id: notification.tipo === 'urgent' ? 'urgent' : 'default',
            sound: 'default',
            icon: 'ic_notification',
            color: notification.tipo === 'urgent' ? '#FF5252' : '#2196F3'
          }
        },
        apns: {
          payload: {
            aps: {
              alert: {
                title: notification.tipo === 'urgent' ? `🚨 URGENTE: ${notification.título}` : notification.título,
                body: notification.descripción || ''
              },
              sound: 'default',
              badge: 1
            }
          }
        }
      }));

      // Enviar a FCM en lotes
      const chunkSize = 500; // FCM permite hasta 500 por request
      const results = [];
      
      for (let i = 0; i < messages.length; i += chunkSize) {
        const chunk = messages.slice(i, i + chunkSize);
        
        // FCM v1 API only accepts a single token per request, so send one per token
        for (const msg of chunk) {
          try {
            const response = await fetch(`https://fcm.googleapis.com/v1/projects/${this.FCM_PROJECT_ID}/messages:send`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${await this.getAccessToken()}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                message: {
                  token: msg.to,
                  notification: {
                    title: msg.notification.title,
                    body: msg.notification.body
                  },
                  data: msg.data || {}
                }
              }),
            });

            const result = await response.json();
            results.push({ success: response.ok, ...result });
          } catch (error) {
            results.push({ success: false, error: error.message });
          }
        }
      }

      console.log(`Notificaciones FCM enviadas: ${results.length}`);
      return results;
    } catch (error) {
      console.error('Error enviando notificaciones FCM:', error);
      throw error;
    }
  }

  // Obtener Access Token para FCM (necesitas implementar esto según tu método de autenticación)
  static async getAccessToken() {
    // Opción 1: Si usas service account en el frontend (NO RECOMENDADO para producción)
    // return 'YOUR_ACCESS_TOKEN';
    
    // Opción 2: Llamar a tu backend que tiene la service account
    try {
      const response = await fetch('/api/fcm/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('No se pudo obtener access token');
      }
      
      const data = await response.json();
      return data.access_token;
    } catch (error) {
      console.error('Error obteniendo access token:', error);
      throw error;
    }
  }

  // Crear notificación con filtros avanzados
  static async createAndSendNotification(notificationData, filters = {}) {
    try {
      console.log('🎯 Iniciando proceso de notificación...', { filters, titulo: notificationData.título });
      
      // 1. Crear la notificación en Firestore
      const docRef = await addDoc(collection(db, 'notifications'), {
        ...notificationData,
        fecha: serverTimestamp(),
        visible: true,
        createdAt: serverTimestamp(),
        filters: filters // Guardar filtros aplicados
      });

      const notificationWithId = {
        id: docRef.id,
        ...notificationData
      };

      // 2. Obtener tokens con filtros
      const targetTokens = await this.getTargetTokens(notificationWithId, filters);
      
      console.log(`📱 Tokens encontrados: ${targetTokens.length}`);
      
      if (targetTokens.length === 0) {
        console.log('No hay usuarios con tokens para recibir esta notificación');
        return {
          success: true,
          notificationId: docRef.id,
          pushNotificationsSent: 0,
          targetTokens: 0,
          message: 'Notificación creada pero no hay usuarios con tokens válidos'
        };
      }

      // 3. Determinar método de envío (FCM o Expo)
      let pushResults = [];
      let sentCount = 0;
      
      if (filters.useExpo || notificationData.useExpo) {
        // Usar Expo Push API
        console.log('📤 Enviando via Expo...');
        pushResults = await this.sendExpoPushNotifications(targetTokens, notificationWithId);
        sentCount = pushResults.filter(r => r.status === 'ok').length;
      } else {
        // Usar FCM por defecto
        console.log('📤 Enviando via FCM...');
        pushResults = await this.sendFCMNotifications(targetTokens, notificationWithId);
        sentCount = pushResults.length;
      }

      // 4. Registrar estadísticas detalladas
      const stats = {
        notificationId: docRef.id,
        totalTokens: targetTokens.length,
        sentCount: sentCount,
        filtersApplied: filters,
        platformBreakdown: this.getPlatformBreakdown(targetTokens),
        sentAt: serverTimestamp(),
        notificationType: notificationData.tipo,
        method: filters.useExpo ? 'expo' : 'fcm'
      };

      await addDoc(collection(db, 'notificationStats'), stats);

      const result = {
        success: true,
        notificationId: docRef.id,
        pushNotificationsSent: sentCount,
        targetTokens: targetTokens.length,
        platformBreakdown: stats.platformBreakdown,
        results: pushResults,
        filters: filters
      };
      
      console.log('🎉 Proceso completado:', result);
      return result;

    } catch (error) {
      console.error('❌ Error creando y enviando notificación:', error);
      throw error;
    }
  }

  // Obtener breakdown por plataforma
  static getPlatformBreakdown(tokens) {
    const breakdown = {
      ios: 0,
      android: 0,
      unknown: 0
    };

    tokens.forEach(token => {
      const platform = token.platform || 'unknown';
      breakdown[platform] = (breakdown[platform] || 0) + 1;
    });

    return breakdown;
  }

  // Enviar notificación de prueba con filtros
  static async sendTestNotification(filters = {}) {
    try {
      const testNotification = {
        título: '🧪 Prueba de Notificación AMPA',
        descripción: 'Esta es una notificación de prueba del portal de administración. Si la recibes, todo funciona correctamente.',
        tipo: 'general',
        visible: false // No visible en la app, solo push
      };

      return await this.createAndSendNotification(testNotification, filters);
    } catch (error) {
      console.error('Error enviando notificación de prueba:', error);
      throw error;
    }
  }
}

export default AdminNotificationService;